/**
 * Initialize the taxonomy filters.
 *
 * @param {Function} callback The callback function to execute after initialization.
 */
const init = (callback) => {
	// Event listener for checkbox change
	document
		.querySelectorAll('.js-gsfc-filter-tax-checkbox')
		.forEach((checkbox) => {
			checkbox.addEventListener('change', async () => {
				await processCheckbox(checkbox);
				callback();
			});
		});

	// Event listener for select change
	document
		.querySelectorAll('.js-gsfc-filter-tax-select')
		.forEach((select) => {
			select.addEventListener('change', async () => {
				await processSelect(select);
				callback();
			});
		});

	// Event listener for multiple select change
	document
		.querySelectorAll('.js-gsfc-filter-tax-multiple-select')
		.forEach((select) => {
			select.addEventListener('change', async () => {
				await processMultipleSelect(select);
				callback();
			});
		});

	// Event listener for taxonomy link click
	document.querySelectorAll('.js-gsfc-filter-tax-link').forEach((link) => {
		link.addEventListener('click', async (e) => {
			e.preventDefault();
			await processLink(link);
			callback();
		});
	});
};

/**
 * Update the taxonomy filters based on global filters.
 */
const update = () => {
	// Update checkbox filters
	document
		.querySelectorAll('.js-gsfc-filter-tax-checkbox')
		.forEach((checkbox) => {
			updateCheckbox(checkbox);
		});

	// Update select filters
	document
		.querySelectorAll('.js-gsfc-filter-tax-select')
		.forEach((select) => {
			updateSelect(select);
		});

	// Update multiple select filters
	document
		.querySelectorAll('.js-gsfc-filter-tax-multiple-select')
		.forEach((select) => {
			updateMultipleSelect(select);
		});

	// Update taxonomy link filters
	document.querySelectorAll('.js-gsfc-filter-tax-link').forEach((link) => {
		updateLink(link);
	});
};

/**
 * Process the taxonomy checkbox filter.
 *
 * @param {HTMLElement} checkbox The checkbox element.
 * @return {Promise} A Promise that resolves after processing the filter.
 */
const processCheckbox = (checkbox) => {
	return new Promise((resolve) => {
		const tax = checkbox.dataset.tax;
		const value = checkbox.value;
		let title = checkbox.nextElementSibling.textContent.trim();
		title = title.replace(/<\/?span[^>]*>/g, '');
		title = title.replace(/\(.*?\)/g, '');
		const selected = checkbox.checked;

		if (!global.filters.taxonomies[tax]) {
			global.filters.taxonomies[tax] = {
				parent: checkbox.closest('.form-group').dataset.parentTerm,
				terms: [],
			};
		}

		if (!selected) {
			const index = global.filters.taxonomies[tax].terms.findIndex(
				(item) => item[0] === value
			);
			if (index > -1) {
				global.filters.taxonomies[tax].terms.splice(index, 1);
			}
		} else {
			global.filters.taxonomies[tax].terms.push([value, title]);
		}
		resolve();
	});
};

/**
 * Process the taxonomy select filter.
 *
 * @param {HTMLElement} select The select element.
 * @return {Promise} A Promise that resolves after processing the filter.
 */
const processSelect = (select) => {
	return new Promise((resolve) => {
		const tax = select.dataset.tax;
		const value = select.value;
		const title = select.options[select.selectedIndex].textContent.trim();

		if (!global.filters.taxonomies[tax]) {
			global.filters.taxonomies[tax] = {
				parent: select.closest('.govuk-form-group').dataset.parentTerm,
				terms: [[]],
			};
		}

		if (value === 'all') {
			delete global.filters.taxonomies[tax];
		} else if (value) {
			global.filters.taxonomies[tax].terms = [[value, title]];
		}
		resolve();
	});
};

/**
 * Process the taxonomy multiple select filter.
 *
 * @param {HTMLElement} select The multiple select element.
 * @return {Promise} A Promise that resolves after processing the filter.
 */
const processMultipleSelect = (select) => {
	return new Promise((resolve) => {
		const tax = select.dataset.tax;
		const options = select.selectedOptions;

		if (!global.filters.taxonomies[tax]) {
			global.filters.taxonomies[tax] = {
				parent: select.closest('.govuk-form-group').dataset.parentTerm,
				terms: [[]],
			};
		}

		const terms = [];
		for (const option of options) {
			if (option.value !== 'all') {
				const title = option.textContent.trim();
				terms.push([option.value, title]);
			}
		}

		if (terms.length === 0) {
			delete global.filters.taxonomies[tax];
		} else {
			global.filters.taxonomies[tax].terms = terms;
		}
		resolve();
	});
};

/**
 * Process the taxonomy link filter.
 *
 * @param {HTMLElement} link The link element.
 * @return {Promise} A Promise that resolves after processing the filter.
 */
const processLink = (link) => {
	return new Promise((resolve) => {
		const tax = link.dataset.tax;
		const value = link.dataset.value;
		const title = link.dataset.title;

		if (value === 'all') {
			delete global.filters.taxonomies[tax];
		} else if (value) {
			global.filters.taxonomies[tax].terms = [[value, title]];
		}

		document
			.querySelectorAll('.js-gsfc-filter-tax-link')
			.forEach((otherLink) => {
				otherLink.classList.remove('active');
			});
		link.classList.add('active');

		resolve();
	});
};

/**
 * Update the taxonomy checkbox filter based on global filters.
 *
 * @param {HTMLElement} checkbox The checkbox element.
 */
const updateCheckbox = (checkbox) => {
	const tax = checkbox.dataset.tax;
	const value = checkbox.value;

	if (global.filters.taxonomies[tax]) {
		const index = global.filters.taxonomies[tax].terms.findIndex(
			(item) => item[0] === value
		);
		checkbox.checked = index > -1;
	}
};

/**
 * Update the taxonomy select filter based on global filters.
 *
 * @param {HTMLElement} select The select element.
 */
const updateSelect = (select) => {
	const tax = select.dataset.tax;

	if (global.filters.taxonomies[tax]) {
		let existing = '';
		Array.from(select.options).forEach((option) => {
			const value = option.value;
			const index = global.filters.taxonomies[tax].terms.findIndex(
				(item) => item[0] === value
			);
			if (index > -1) {
				existing = value;
			}
		});
		select.value = existing || 'all';
	}
};

/**
 * Update the taxonomy multiple select filter based on global filters.
 *
 * @param {HTMLElement} select The multiple select element.
 */
const updateMultipleSelect = (select) => {
	const tax = select.dataset.tax;

	if (global.filters.taxonomies[tax]) {
		Array.from(select.options).forEach((option) => {
			const value = option.value;
			const index = global.filters.taxonomies[tax].terms.findIndex(
				(item) => item[0] === value
			);
			option.selected = index > -1;
		});
	}
	if (
		global.filters.taxonomies[tax] &&
		global.filters.taxonomies[tax].terms.length === 0
	) {
		select.querySelector("option[value='all']").selected = true;
	}
};

/**
 * Update the taxonomy link filter based on global filters.
 *
 * @param {HTMLElement} link The link element.
 */
const updateLink = (link) => {
	const tax = link.dataset.tax;
	const value = link.dataset.value;

	if (global.filters.taxonomies[tax]) {
		const index = global.filters.taxonomies[tax].terms.findIndex(
			(item) => item[0] === value
		);
		link.classList.toggle('active', index > -1);
	}
};

// Export initialization and update functions
export default { init, update };
